/**
 * BookingFlowShell — shared presentational chrome for production BookingFlow
 * and Draft Builder preview.
 *
 * Owns: theme vars, FlowTopBar, BuildPass, step title, scroll column,
 * FlowDock, inner ScreenStack, and ConfirmScene handoff.
 *
 * Does NOT own: booking state, Stripe, availability, writes, or step content.
 * Parents supply step content + dock/confirm behavior via props.
 *
 * Visual values must stay aligned with BookingFlow's production chrome and
 * design-imports/. Do not invent Draft-only sizing here.
 */

import React, { useEffect, type ReactNode } from 'react';
import {
  FlowTopBar,
  FlowDock,
  BuildPass,
  ScreenStack,
  type BuildRowData,
} from '@/components/booking-flow/FlowScaffold';
import {
  ConfirmScene,
  type PassBooking,
} from '@/components/booking-flow/ConfirmationPass';
import {
  flowThemeVars,
  flowTailwindOverrides,
  loadThemeFonts,
  FLOW_DEFAULTS,
  FLOW_COLUMN_MAX,
  type BookingTheme,
} from '@/components/booking-flow/themes';

export interface BookingFlowShellProps {
  theme: BookingTheme;
  businessName: string;
  /** 1-based step index shown in the top bar / progress */
  step: number;
  totalSteps: number;
  title: string;
  buildRows?: BuildRowData[];
  children: ReactNode;
  onBack: () => void;

  /** When false, the bottom dock is not rendered */
  showDock?: boolean;
  cta?: string;
  ctaDisabled?: boolean;
  ctaSub?: string;
  onCta?: () => void;

  /** Error banner above the dock (production booking errors) */
  error?: string | null;

  /** Keys that must not snapshot during ScreenStack transitions */
  noSnapshotKeys?: string[];

  /** When true, flow chrome fades out and ConfirmScene is shown */
  showConfirmation?: boolean;
  confirmStage?: 'confirming' | 'tray' | 'expanded';
  confirmBooking?: PassBooking | null;
  onConfirmTap?: () => void;
  onConfirmClose?: () => void;
  onManage?: () => void;
  onAddToCalendar?: () => void;
  businessTagline?: string | null;
}

export function BookingFlowShell({
  theme,
  businessName,
  step,
  totalSteps,
  title,
  buildRows = [],
  children,
  onBack,
  showDock = true,
  cta = 'Continue',
  ctaDisabled,
  ctaSub,
  onCta,
  error,
  noSnapshotKeys,
  showConfirmation = false,
  confirmStage = 'confirming',
  confirmBooking = null,
  onConfirmTap,
  onConfirmClose,
  onManage,
  onAddToCalendar,
  businessTagline,
}: BookingFlowShellProps) {
  useEffect(() => {
    loadThemeFonts(theme);
  }, [theme]);

  const themeStyleVars = {
    ...flowThemeVars(theme),
    ...flowTailwindOverrides(theme),
  };

  const hasBuildRows = buildRows.length > 0;
  const inFlow = !showConfirmation;

  const flowScreen = (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: `var(--f-bg, ${FLOW_DEFAULTS.bg})`,
        display: 'flex',
        flexDirection: 'column',
        color: `var(--f-ink, ${FLOW_DEFAULTS.ink})`,
      }}
    >
      <FlowTopBar
        step={step}
        totalSteps={totalSteps}
        theme={theme}
        businessName={businessName}
        onBack={onBack}
      />

      <div
        className="booking-flow-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '8px 24px 0',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div style={{ maxWidth: FLOW_COLUMN_MAX - 48, margin: '0 auto' }}>
          {hasBuildRows && (
            <div style={{ paddingTop: 4, marginBottom: 22 }}>
              <BuildPass theme={theme} rows={buildRows} />
            </div>
          )}

          <div style={{ paddingTop: 6 }}>
            <h2
              style={{
                margin: 0,
                fontFamily: theme.display,
                fontStyle: theme.displayItalic ? 'italic' : 'normal',
                fontWeight: theme.displayWeight,
                fontSize: 30,
                lineHeight: 1.0,
                letterSpacing: '-0.02em',
                color: `var(--f-ink, ${FLOW_DEFAULTS.ink})`,
                textWrap: 'pretty' as const,
              }}
            >
              {title}
            </h2>
          </div>

          <div style={{ paddingTop: 28, paddingBottom: 24 }}>{children}</div>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '0 24px 8px',
            maxWidth: FLOW_COLUMN_MAX,
            margin: '0 auto',
            width: '100%',
          }}
        >
          <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        </div>
      )}

      {showDock && onCta && (
        <div style={{ maxWidth: FLOW_COLUMN_MAX, margin: '0 auto', width: '100%' }}>
          <FlowDock
            cta={cta}
            ctaDisabled={ctaDisabled}
            ctaSub={ctaSub}
            onCta={onCta}
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{
        position: 'relative',
        height: '100dvh',
        overflow: 'hidden',
        background: theme.flowBg || FLOW_DEFAULTS.bg,
        fontFamily: '"Inter Tight", system-ui, sans-serif',
        color: theme.flowInk || FLOW_DEFAULTS.ink,
        ...themeStyleVars,
      } as React.CSSProperties}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: inFlow ? 1 : 0,
          transform: inFlow ? 'scale(1)' : 'scale(0.985)',
          transition: 'opacity 360ms ease, transform 520ms cubic-bezier(0.22,1,0.36,1)',
          pointerEvents: inFlow ? 'auto' : 'none',
        }}
      >
        <ScreenStack
          screenKey={`step-${step}`}
          order={step}
          noSnapshotKeys={noSnapshotKeys}
        >
          {flowScreen}
        </ScreenStack>
      </div>

      {showConfirmation && (
        <ConfirmScene
          stage={confirmStage}
          theme={theme}
          visible={true}
          booking={confirmBooking}
          onTap={onConfirmTap ?? (() => {})}
          onClose={onConfirmClose ?? (() => {})}
          onManage={onManage}
          onAddToCalendar={onAddToCalendar}
          businessName={businessName}
          businessTagline={businessTagline}
        />
      )}
    </div>
  );
}
